import { useEffect, useRef, useState } from 'react';
import {
  useGetProjectNotesQuery,
  useCreateNoteMutation,
  usePatchNoteMutation,
  useDeleteNoteMutation,
} from 'app/services/projectBoardApi';
import type { IProjectNote } from 'models';
import { useTabAccess } from '../../hooks/useProjectBoardData';
import {
  FilesNotesDeletePopup,
  RowActionsDropdown,
  scheduleTitleFocus,
  caretToEnd,
} from '../files/filesNotesShared';

/**
 * NOTES TAB (`#notes` — `notes/view/notes.js` + notes.tmpl), inventory §B5 /
 * parity 176–181. Same split-panel chassis as Files minus uploads.
 *
 * + ADD (parity 176): POST {project_id, note_name:'Note'} → the new note
 * joins the list, is selected, and its title focuses after 1s
 * (active_last_change).
 *
 * note_name (parity 177): the right-pane title input toggles `.invalid` live
 * below 3 chars; its change SILENTLY rejects <3 (no save, typed value kept
 * until the next echo re-render). The sidebar rename-in-place input REVERTS
 * to the model value on a <3 change; a valid change saves + re-activates the
 * tab (with the 1s title focus active_last_change carries).
 *
 * note_text (parity 178 / OQ B-5, per the BUILD ruling): the contenteditable
 * body saves its RAW HTML on blur and the stored HTML is re-injected on
 * render (the legacy `.append(obj.note_text)`) — replicated FAITHFULLY via
 * innerHTML with NO sanitization dependency added; the stored-markup XSS
 * exposure is a documented PARKED ITEM. No client length cap (the body is not
 * a `.textarea`, so the global maxlength guard does not apply).
 *
 * PERMISSION GATING (parity 181 / OQ B-4, settled like Files/3e): UI gates by
 * the **notes** tab flag with WORKING read-only (inputs disabled, buttons
 * non-clickable, contenteditable off) — the legacy's pre-render disable block
 * is broken [NEG]; the API authorizes by the 'project' tab flag (server 403).
 *
 * [NEG]s honored: `click_note_name` (undefined method), the commented-out
 * `blur .note-input` re-readonly (the sidebar input ships writable — no
 * readonly attr in the template), dead `saveNotesFieldData`/`#cancel`
 * bindings, the "Reset Use Defined Field?" icon-click popup mode, the legacy
 * empty-hash `location.href += 'notes'` rewrite, and no realtime sync.
 */

const NoteSidebarInput = ({
  note,
  access,
  onCommit,
}: {
  note: IProjectNote;
  access: boolean;
  onCommit: (value: string) => void;
}) => {
  const synced = note.note_name ?? '';
  const [draft, setDraft] = useState(synced);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(synced);
  }, [synced]);

  // change_note_name: <3 chars reverts to the model value, else save (patch).
  const commit = () => {
    if (draft === synced) return;
    if (draft.length < 3) {
      setDraft(synced);
      return;
    }
    onCommit(draft);
  };

  return (
    <input
      ref={ref}
      value={draft}
      className="note-input"
      data-id={note.id}
      disabled={!access}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
};

const NoteTitleInput = ({
  note,
  access,
  onCommit,
}: {
  note: IProjectNote;
  access: boolean;
  onCommit: (value: string) => void;
}) => {
  const synced = note.note_name ?? '';
  const [draft, setDraft] = useState(synced);
  const [invalid, setInvalid] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(synced);
  }, [synced]);

  // updateNotesField: a <3 note_name is rejected SILENTLY (no save, no revert).
  const commit = () => {
    if (draft === synced || draft.length < 3) return;
    onCommit(draft);
  };

  return (
    <input
      ref={ref}
      onFocus={(e) => caretToEnd(e.target)}
      type="text"
      id={`title_notes_field_${note.id}`}
      name="note_name"
      className={`col-xs- textarea-field-title notes_field title_notes_field${
        invalid ? ' invalid' : ''
      }`}
      minLength={3}
      maxLength={65}
      value={draft}
      data-id={note.id}
      disabled={!access}
      onChange={(e) => {
        // inputNoteName: live `.invalid` toggle below 3 chars (parity 177).
        setDraft(e.target.value);
        setInvalid(e.target.value.length < 3);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
};

/**
 * Contenteditable body — RAW HTML round-trip (see header comment). Injection
 * is imperative (the legacy post-render `.append(note_text)`); the cache echo
 * only re-syncs while the body is not focused.
 */
const NoteBody = ({
  note,
  access,
  onCommit,
}: {
  note: IProjectNote;
  access: boolean;
  onCommit: (html: string) => void;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const synced = (note.note_text as string | null) ?? '';

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== synced) {
      // PARKED RISK (OQ B-5): stored markup renders as HTML — faithful to the
      // legacy XSS-ish behavior; no sanitizer dependency added by ruling.
      el.innerHTML = synced;
    }
  }, [synced]);

  return (
    <div
      ref={ref}
      className="col-xs-12 content-editable textarea-field content_notes_field"
      name="note_text"
      data-id={note.id}
      contentEditable={access}
      suppressContentEditableWarning
      id={`content_notes_field_${note.id}`}
      onBlur={() => {
        const html = ref.current?.innerHTML ?? '';
        if (html !== synced) onCommit(html);
      }}
    />
  );
};

const NotesTab = ({ projectId, active }: { projectId: number; active: boolean }) => {
  const { data: notes } = useGetProjectNotesQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const access = useTabAccess(projectId, 'notes');
  const [createNote] = useCreateNoteMutation();
  const [patchNote] = usePatchNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteFor, setDeleteFor] = useState<IProjectNote | null>(null);
  const selectedRef = useRef<number | null>(null);
  selectedRef.current = selectedId;

  // First-note auto-open on load and on every tab re-entry (parity 180).
  const prevActive = useRef(false);
  const bootSelected = useRef(false);
  useEffect(() => {
    if (!notes) {
      prevActive.current = active;
      return;
    }
    const entered = active && !prevActive.current;
    if ((active && !bootSelected.current) || entered) {
      bootSelected.current = true;
      setSelectedId(notes.length ? notes[0].id : null);
    }
    prevActive.current = active;
  }, [active, notes]);

  // addNotes: POST → select the new note + 1s title focus (parity 176).
  const onAdd = () => {
    if (!access) return;
    createNote({ project_id: projectId, note_name: 'Note' })
      .unwrap()
      .then((row) => {
        setSelectedId(row.id);
        scheduleTitleFocus(`title_notes_field_${row.id}`);
      })
      .catch(() => undefined);
  };

  // updateNotesField success → active_last_change(activeID): re-activate +
  // 1s title focus of the ACTIVE tab.
  const refocusActive = () => {
    const id = selectedRef.current;
    if (id != null) scheduleTitleFocus(`title_notes_field_${id}`);
  };

  const onTitleCommit = (id: number, value: string) => {
    patchNote({ id, projectId, body: { note_name: value } })
      .unwrap()
      .then(refocusActive)
      .catch(() => undefined);
  };

  const onBodyCommit = (id: number, html: string) => {
    patchNote({ id, projectId, body: { note_text: html } })
      .unwrap()
      .then(refocusActive)
      .catch(() => undefined);
  };

  // change_note_name success → active_last_change(id): activate THAT note +
  // 1s title focus.
  const onSidebarCommit = (id: number, value: string) => {
    patchNote({ id, projectId, body: { note_name: value } })
      .unwrap()
      .then(() => {
        setSelectedId(id);
        scheduleTitleFocus(`title_notes_field_${id}`);
      })
      .catch(() => undefined);
  };

  // rename_note: focus the sidebar input with the caret at the end (the
  // readonly strip is a no-op — the input ships writable, [NEG]-quirk).
  const onRenameClick = (id: number) => {
    const input = document.querySelector<HTMLInputElement>(
      `input.note-input[data-id="${id}"]`
    );
    if (!input) return;
    input.focus();
    const len = input.value.length;
    input.setSelectionRange(len, len);
  };

  // removeFileField (notes twin): optimistic destroy + render(true) — the
  // FIRST remaining note is selected after a delete (parity 179).
  const onConfirmDelete = () => {
    if (!deleteFor) return;
    const deletedId = deleteFor.id;
    deleteNote({ id: deletedId, projectId });
    setDeleteFor(null);
    const remaining = (notes ?? []).filter((n) => n.id !== deletedId);
    setSelectedId(remaining.length ? remaining[0].id : null);
  };

  if (!notes) return null; // Backbone renders only after the collection fetch

  return (
    <div id="notes_format" className=" tab-content files-tab__content content">
      {/* Inner id="files" is the live template's copy-paste relic — kept. */}
      <div id="files" className="col-xs-12 full-height ">
        <div className=" full-height">
          <div className="row full-height">
            <div className="col-xs-3 full-height left-column files-content-files-format">
              <div className="row block-header">
                <div className="col-xs-12 padding-right-0">
                  <div className="row">
                    <div className="col-xs-1 account-icon padding-left-0">
                      {' '}
                      <i className="icon-note-edit" />
                    </div>
                    <div className="col-xs-7 block-header-title padding-left-0 padding-top-14">
                      <span className="block-title ">Notes</span>
                    </div>
                    <div className="col-xs-4 block-header-button">
                      <button
                        id="add_notes_field"
                        className={`btn-add btn add${access ? '' : ' non-clickable'}`}
                        onClick={onAdd}
                      >
                        + ADD
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row notes-list">
                <div className="col-xs-12 full-height">
                  <div className="notes-scroll row">
                    <ul
                      className="nav nav-tabs notes-block notes-block-item row margin-right-0"
                      id="myTab"
                      role="tablist"
                    >
                      {notes.map((n) => (
                        <li
                          key={n.id}
                          className={`nav-item notes-tab col-xs-12${
                            selectedId === n.id ? ' active ' : ''
                          }`}
                          id={`note_field_${n.id}`}
                        >
                          <div className=" margin-right-0">
                            <div className="col-xs-12">
                              <a
                                className="files-fields files-fields-notes"
                                id={`note_name_field_${n.id}-tab`}
                                data-toggle="tab"
                                href={`#note_name_field_${n.id}`}
                                role="tab"
                                aria-controls={`note_name_field_${n.id}-tab`}
                                aria-selected="true"
                                data-id={n.id}
                                onMouseDown={(e) => {
                                  // changeNotesFild: same-tab early return; the
                                  // 1s title focus is skipped when the press is
                                  // on the writable rename input or Rename item.
                                  if (n.id === selectedId) return;
                                  const t = e.target as HTMLElement;
                                  if (t.closest('.note-input') || t.closest('.rename'))
                                    return;
                                  scheduleTitleFocus(`title_notes_field_${n.id}`);
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedId(n.id);
                                }}
                              >
                                <NoteSidebarInput
                                  note={n}
                                  access={access}
                                  onCommit={(value) => onSidebarCommit(n.id, value)}
                                />
                              </a>
                              <RowActionsDropdown
                                id={n.id}
                                dropdownClassName="dropdown_notes_fields"
                                topOffset={32}
                                leftOffset={-145}
                                disabled={!access}
                                openOnHover
                              >
                                {(close) => (
                                  <>
                                    <li>
                                      <a
                                        tabIndex={-1}
                                        href="#"
                                        data-id={n.id}
                                        className="rename"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          close();
                                          onRenameClick(n.id);
                                        }}
                                      >
                                        <svg
                                          className="rename-icon"
                                          viewBox="0 0 24 24"
                                          width="24"
                                          height="18"
                                        >
                                          <path
                                            fill="#9e9e9e"
                                            d="M15 16L11 20H21V16H15M12.06 7.19L3 16.25V20H6.75L15.81 10.94L12.06 7.19M18.71 8.04C19.1 7.65 19.1 7 18.71 6.63L16.37 4.29C16.17 4.09 15.92 4 15.66 4C15.41 4 15.15 4.1 14.96 4.29L13.13 6.12L16.88 9.87L18.71 8.04Z"
                                          />
                                        </svg>
                                        Rename
                                      </a>
                                    </li>
                                    <li>
                                      <a
                                        tabIndex={-1}
                                        href="#"
                                        data-id={n.id}
                                        className="remove"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          close();
                                          setDeleteFor(n);
                                        }}
                                      >
                                        <i className="icon-trash" />
                                        Delete
                                      </a>
                                    </li>
                                  </>
                                )}
                              </RowActionsDropdown>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xs-9 full-height righ-column   content-item">
              <div className="row row-content-item  full-height">
                <div
                  className="col-xs-12  tab-content full-height notes-block-item"
                  id="myTabContent"
                >
                  {notes.map((n) => (
                    <div
                      key={n.id}
                      className={`full-height row tab-pane fade note_name_field_${n.id}${
                        selectedId === n.id ? ' active in' : ''
                      }`}
                      id={`note_name_field_${n.id}`}
                      role="tabpanel"
                      aria-labelledby={`note_name_field_${n.id}-tab`}
                    >
                      <div className="col-xs-12 content-header">
                        <div className="col-xs-6 content-title">
                          <NoteTitleInput
                            note={n}
                            access={access}
                            onCommit={(value) => onTitleCommit(n.id, value)}
                          />
                        </div>
                        <div className="col-xs-6">
                          <div className="row">
                            <div className="col-xs-7" />
                            <div className="col-xs-5">
                              <div className="row upload-dounload-block">
                                <div className="col-xs-12 text-right" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12 content-tooltip">
                        <div className="col-xs-6 title-tooltip">
                          <span>3 - 65 characters</span>
                        </div>
                      </div>
                      <div className="col-xs-12 content-files full-height">
                        <NoteBody
                          note={n}
                          access={access}
                          onCommit={(html) => onBodyCommit(n.id, html)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {deleteFor && (
        <FilesNotesDeletePopup
          title="Delete Note?"
          content="If you choose to Delete, all Note Data will be lost and will not be recoverable ."
          name={deleteFor.note_name}
          onConfirm={onConfirmDelete}
          onClose={() => setDeleteFor(null)}
        />
      )}
    </div>
  );
};

export default NotesTab;
